// The booking action and its link (contract section 14). Foundation only: the one provider is `link`.
/** @typedef {import('./typedefs.js').BookingAction} BookingAction */
/** @typedef {import('./typedefs.js').BookingDoc} BookingDoc */
/** @typedef {import('./typedefs.js').BookingTarget} BookingTarget */
/** @typedef {import('./typedefs.js').LinkContext} LinkContext */

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const own = (o, k) => (isObj(o) && Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined);
/** A button text that says something: a string, or a text keyed by language with at least one word in it. */
const worded = (v) => (typeof v === 'string' ? v.trim() !== '' : isObj(v) && Object.entries(v).some(([k, s]) => k !== '_auto' && typeof s === 'string' && s.trim() !== ''));

/**
 * The booking action for a unit, unit type or place. Resolution order: the unit, then its type, then the place,
 * then the map's default, then the legacy content `link` (which becomes `fallbackUrl`). Renamed keys resolve
 * through `booking.aliases`. Returns null when nothing applies (no button).
 * A more specific entry OVERRIDES the one behind it, field by field (contract section 14, "a per-unit entry
 * overrides its type"): a unit with its own link keeps its type's button text, a unit with its own text keeps its
 * type's link (WS-51: the unit's entry used to replace the type's whole action, and its button lost its words).
 * The map's default and the legacy link stand in only when no entry applies at all, as before.
 * @param {BookingTarget | string} target  a key, or `{ unit, type, place, link }`
 * @param {BookingDoc | null} [booking]    the bundle's `booking` section
 * @returns {BookingAction | null}
 */
export function bookingAction(target, booking) {
  const tg = typeof target === 'string' ? { unit: target } : isObj(target) ? target : {};
  const keys = [tg.unit, tg.type, tg.place].filter((k) => typeof k === 'string' && k);
  const asked = keys[0] || null;
  const shape = (a, key) => ({
    target: key,
    provider: 'link', // the one provider of this build: whatever a document names is read as `link`
    refs: isObj(a.refs) ? { ...a.refs } : {},
    fallbackUrl: typeof a.fallbackUrl === 'string' ? a.fallbackUrl : null,
    label: a.label ?? null,
  });
  const found = [];
  for (const k of keys) {
    const key = typeof own(booking?.aliases, k) === 'string' ? booking.aliases[k] : k;
    const a = own(booking?.targets, key);
    if (isObj(a)) found.push([a, key]);
  }
  if (found.length) {
    const first = (field, set) => found.map(([a]) => a[field]).find(set) ?? null;
    return { ...shape(...found[0]), fallbackUrl: first('fallbackUrl', (v) => typeof v === 'string' && v !== ''), label: first('label', worded) };
  }
  if (isObj(booking?.default) && asked) return shape(booking.default, asked);
  if (typeof tg.link === 'string' && tg.link && asked) return shape({ fallbackUrl: tg.link }, asked);
  return null;
}

/**
 * The address a booking action opens, or null when it has none that may be opened. Built with the `URL` API
 * (no string joining): absolute `https` only, no credentials in the address.
 * @param {BookingAction | null} action
 * @param {LinkContext} [ctx]  reserved for providers that take the stay or the language; `link` uses none of it
 * @returns {string | null}
 */
export function buildLink(action, ctx) {
  if (!isObj(action) || typeof action.fallbackUrl !== 'string') return null;
  let url;
  try { url = new URL(action.fallbackUrl.trim()); } catch { return null; }
  if (url.protocol !== 'https:' || url.username || url.password || !url.hostname) return null;
  return url.href;
}
